import { type Component, For, Show } from "solid-js";

import { deleteAttachment, uploadAttachment } from "@/api/attachments";
import AttachmentChip from "./AttachmentChip";
import {
  type AttachmentsStore,
  type PendingAttachment,
  newTempId,
} from "./attachmentsStore";

export interface AttachmentPickerProps {
  store: AttachmentsStore;
  conversationId: string | null;
  /** True while a chat stream is in flight — disables picking + removing. */
  disabled?: boolean;
  /** MIME / extension allowlist for the file input. Matches BE accept set. */
  accept?: string;
  /**
   * Receives a callback that opens the file picker dialog. Lets the
   * slash menu (`/file`) trigger picking without rendering the chip
   * button itself. Called once on mount.
   */
  triggerRef?: (trigger: () => void) => void;
  /** Hide the visible chip button; the picker still works via triggerRef. */
  chipHidden?: boolean;
}

const DEFAULT_ACCEPT =
  ".pdf,.txt,.md,.csv,.json,application/pdf,text/plain,text/markdown,text/csv,application/json";

function errorMessage(e: unknown): string {
  if (e && typeof e === "object" && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return "upload failed";
}

const AttachmentPicker: Component<AttachmentPickerProps> = (props) => {
  let inputEl: HTMLInputElement | undefined;

  const pickFiles = () => {
    if (props.disabled) return;
    inputEl?.click();
  };

  // Lets the slash menu (`/file`) and other surfaces trigger the
  // picker without rendering the chip button.
  props.triggerRef?.(pickFiles);

  const onFileChange = async (e: Event) => {
    const target = e.currentTarget as HTMLInputElement;
    // Defense in depth: pickFiles() blocks click when disabled, but a
    // programmatic change event (autofill, browser extensions) can
    // bypass that path. Always re-check before kicking off uploads.
    if (props.disabled) {
      target.value = "";
      return;
    }
    const files = target.files ? Array.from(target.files) : [];
    target.value = ""; // allow re-picking the same file later
    for (const file of files) {
      const tempId = newTempId();
      props.store.add({
        id: tempId,
        filename: file.name,
        mime: file.type || "application/octet-stream",
        bytes: file.size,
        status: "uploading",
      });
      try {
        const resp = await uploadAttachment(file, props.conversationId);
        props.store.replace(tempId, {
          id: resp.id,
          filename: resp.filename,
          mime: resp.mime,
          bytes: Number(resp.bytes),
          status: "ready",
        });
      } catch (err) {
        props.store.patch(tempId, {
          status: "failed",
          error: errorMessage(err),
        });
      }
    }
  };

  const removingIds = new Set<string>();
  const removeChip = async (id: string) => {
    // Guard against double-click: a second invocation while the first
    // DELETE is mid-flight finds the chip already removed from the
    // store but might still want to re-issue the BE call. Skip both.
    if (removingIds.has(id)) return;
    removingIds.add(id);
    const entry = props.store.list().find((a) => a.id === id);
    props.store.remove(id);
    // Only call BE delete on ready (server-side) attachments; uploading
    // chips don't exist yet, failed chips have no row to clean up.
    if (entry?.status === "ready") {
      try {
        await deleteAttachment(id);
      } catch {
        // Best-effort: user already saw the chip vanish; surfacing a
        // delete error inline would be more confusing than helpful.
      }
    }
    removingIds.delete(id);
  };

  return (
    <div class="chat-attachments">
      <Show when={!props.chipHidden}>
        <button
          type="button"
          class="chat-attachment-add"
          aria-label="attach file"
          title="attach file"
          onClick={pickFiles}
          disabled={props.disabled}
        >
          📎
        </button>
      </Show>
      <input
        ref={inputEl}
        type="file"
        accept={props.accept ?? DEFAULT_ACCEPT}
        multiple
        hidden
        onChange={(e) => void onFileChange(e)}
      />
      <Show when={props.store.list().length > 0}>
        <ul class="chat-attachment-chips">
          <For each={props.store.list() as readonly PendingAttachment[]}>
            {(a) => (
              <li>
                <AttachmentChip
                  attachment={a}
                  onRemove={(id) => void removeChip(id)}
                />
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
};

export default AttachmentPicker;
